import React from 'react';

import { Box, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import moment from 'moment';

import { MISC_DATE_POLISH_DATE_ONLY_FORMAT } from '../../../core/constants';
import { formatCurrency, formatNumber, formatPreciseCurrency, getChange } from './sales_analysis_utils';
import RevenueLegend from './revenue_legend';
import StatTile from '../../common/stat_tile';

const ChangeBadge = ({ change, invertColors }) => {
    if (change === null || change === undefined || Math.abs(change) < 0.05) return null;

    const isPositive = change > 0;
    const color = (invertColors ? !isPositive : isPositive) ? 'success' : 'error';

    return (
        <Stack direction="row" alignItems="center" columnGap={ 0.75 } flexWrap="wrap">
            <Box sx={ theme => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                px: 0.75,
                borderRadius: 1,
                color: theme.palette[color].main,
                backgroundColor: alpha(theme.palette[color].main, 0.1)
            }) }>
                { isPositive ? <ArrowUpwardIcon sx={ { fontSize: 14 } }/> : <ArrowDownwardIcon sx={ { fontSize: 14 } }/> }
                <Typography variant="body2" sx={ { fontWeight: 600 } }>{ Math.abs(change).toFixed(1).replace('.', ',') }%</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap>vs poprzedni okres</Typography>
        </Stack>
    );
};

const KpiTile = ({ label, value, change, invertColors, loading, icon, color }) => (
    <StatTile
        label={ label }
        value={ value }
        icon={ icon }
        color={ color }
        loading={ loading }
        footer={ !loading && <ChangeBadge change={ change } invertColors={ invertColors }/> }
    />
);

const KpiTiles = ({ summary, loading }) => {
    const current = summary?.current;
    const previous = summary?.previous;

    return (
        <Box>
            <Grid container spacing={ 2 }>
                <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                    <KpiTile label="Obrót brutto" loading={ loading } icon={ <PaymentsIcon/> } color="primary"
                             value={ formatCurrency(current?.revenue) }
                             change={ getChange(current?.revenue, previous?.revenue) }/>
                </Grid>
                <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                    <KpiTile label="Liczba transakcji" loading={ loading } icon={ <ReceiptLongIcon/> } color="info"
                             value={ formatNumber(current?.orders) }
                             change={ getChange(current?.orders, previous?.orders) }/>
                </Grid>
                <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                    <KpiTile label="Średni koszyk" loading={ loading } icon={ <ShoppingBasketIcon/> } color="secondary"
                             value={ formatPreciseCurrency(current?.averageBasket) }
                             change={ getChange(current?.averageBasket, previous?.averageBasket) }/>
                </Grid>
                <Grid item xs={ 12 } sm={ 6 } lg={ 3 }>
                    <KpiTile label="Zamówienia ze zwrotem" loading={ loading } invertColors icon={ <AssignmentReturnIcon/> } color="warning"
                             value={ current ? `${ String(current.returnRate).replace('.', ',') }%` : '-' }
                             change={ getChange(current?.returnRate, previous?.returnRate) }/>
                </Grid>
            </Grid>
            <Stack direction="row" spacing={ 2 } alignItems="baseline" sx={ { mt: 1 } }>
                {
                    summary && (
                        <Typography variant="caption" color="text.secondary">
                            Poprzedni okres: { moment(summary.previousFrom).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) } – { moment(summary.previousTo).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) }
                        </Typography>
                    )
                }
            </Stack>
            <RevenueLegend/>
        </Box>
    );
};

export default KpiTiles;
